"use client";

import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";
import { Loader2, Package } from "lucide-react";
import type * as React from "react";
import { useToolkitPicker } from "../hooks/use-toolkit-picker";

export type ToolkitPickerTriggerProps = React.ComponentProps<typeof Button> & {
  maxVisibleIcons?: number;
  countType?: "toolkits" | "tools";
  loading?: boolean;
};

export function ToolkitPickerTrigger({
  maxVisibleIcons = 2,
  className,
  countType = "toolkits",
  loading = false,
  ...buttonProps
}: ToolkitPickerTriggerProps) {
  const {
    state: { committedToolkits, committedTools },
  } = useToolkitPicker();

  const selectedToolkitNames = Array.from(committedToolkits.keys());
  const toolkitsCount = selectedToolkitNames.length;
  const toolsCount = committedTools.size;

  const visibleToolkits = selectedToolkitNames
    .slice(0, maxVisibleIcons)
    .map((name) => {
      const toolkit = committedToolkits.get(name);
      return { name, Icon: getToolkitIcon(toolkit?.metadata?.id) };
    });
  const overflow = Math.max(toolkitsCount - visibleToolkits.length, 0);
  const showTools = countType === "tools";
  let label = "MCP Servers";
  let count = toolkitsCount;
  if (showTools) {
    label = "Tools";
    count = toolsCount;
  }

  let ariaLabel = `Select ${countType === "tools" ? "Tools" : "MCP Servers"}`;
  if (loading) {
    ariaLabel = "Loading MCP servers...";
  } else if (toolkitsCount > 0) {
    ariaLabel = `${count} ${label.toLowerCase()} selected`;
  }

  return (
    <Button
      {...buttonProps}
      aria-label={ariaLabel}
      className={cn("gap-2 bg-transparent transition-colors", className)}
      disabled={loading}
      size={buttonProps.size ?? "sm"}
      variant={buttonProps.variant ?? "outline"}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <div className="flex items-center gap-2">
            <div className="h-4 w-16 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
            <div className="h-5 w-6 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-700" />
          </div>
        </>
      ) : (
        <>
          {toolkitsCount > 0 && (
            <Tooltip>
              <TooltipTrigger
                render={<span className="mr-2 flex items-center" />}
              >
                {visibleToolkits.map(({ name, Icon }, idx) => (
                  <div
                    className={cn("relative first:ml-0", idx > 0 && "-ml-2")}
                    key={name}
                    style={{ zIndex: idx + 1 }}
                  >
                    <div className="flex size-6 items-center justify-center rounded-xl bg-gradient-to-br from-white to-neutral-50 p-1.5 shadow-lg ring-1 ring-neutral-200/80 transition-all hover:scale-110 hover:shadow-xl dark:from-neutral-800 dark:to-neutral-900 dark:ring-neutral-700/80">
                      <Icon className="h-3 w-3 text-neutral-700 dark:text-neutral-300" />
                    </div>
                  </div>
                ))}
                {overflow > 0 && (
                  <span
                    className="inline-flex h-6 w-6 items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 font-bold text-[11px] text-blue-800 shadow-lg ring-1 ring-blue-200/80 dark:from-blue-900/40 dark:to-blue-800/40 dark:text-blue-200 dark:ring-blue-700/80"
                    style={{ zIndex: visibleToolkits.length + 10 }}
                  >
                    +{overflow}
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>
                <div className="text-xs">
                  <ul className="list-disc space-y-1 pl-4">
                    <li>
                      <span className="font-semibold text-neutral-800 dark:text-neutral-200">
                        MCP Servers
                      </span>
                      <span className="text-neutral-700 dark:text-neutral-300">
                        : {selectedToolkitNames.join(", ")}
                      </span>
                    </li>
                    <li>
                      <span className="font-semibold text-neutral-800 dark:text-neutral-200">
                        Tools
                      </span>
                      <span className="text-neutral-700 dark:text-neutral-300">
                        : {toolsCount} selected
                      </span>
                    </li>
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
          {toolkitsCount === 0 && (
            <>
              <Package className="h-4 w-4" />
              <span className="truncate text-sm">
                {`Select ${countType === "tools" ? "Tools" : "MCP Servers"}`}
              </span>
            </>
          )}

          {toolkitsCount > 0 && (
            <span className="inline-flex items-center gap-2 truncate text-sm">
              {label}
              <Badge className="px-1.5 py-0 text-[11px]" variant="default">
                {count}
              </Badge>
            </span>
          )}
        </>
      )}
    </Button>
  );
}
